/**
 * Graph ids, in a URL.
 *
 * Microsoft Graph folder and message ids are base64 in one of two alphabets: the standard one,
 * which contains `+` and `/`, and the immutable-id one, which is already URL-safe. The first of
 * those cannot go into a path segment. An encoded slash is rejected outright by some proxies and
 * silently decoded into a path separator by others, and both failures look like a broken deep link
 * rather than an encoding problem.
 *
 * So a route id is the raw id when the raw id is already safe — which is every well-known folder
 * name (`inbox`, `archive`) and every immutable id, keeping `/email/inbox/AAMk…` exactly as the
 * design draws it — and an explicitly marked base64url encoding when it is not.
 *
 * The `~` marker is what makes the two cases tellable apart. It is not in either base64 alphabet
 * and no Graph id begins with it, so the decision is never a guess.
 */

const MARKER = '~';
/** Unreserved per RFC 3986, plus `=` and `:`, which are legal in a path segment and appear in ids. */
const URL_SAFE = /^[A-Za-z0-9._~=:-]*$/;

export function toMailRouteId(id: string | null | undefined): string {
   const value = id ?? '';
   if (!value) return '';
   if (URL_SAFE.test(value) && !value.startsWith(MARKER)) return value;
   return MARKER + base64Url(value);
}

export function fromMailRouteId(routeId: string | null | undefined): string {
   const value = routeId ?? '';
   if (!value.startsWith(MARKER)) return value;
   try {
      return fromBase64Url(value.slice(MARKER.length));
   } catch {
      // A hand-edited or truncated link. Returning the raw text lets the read fail as a 404 the
      // page can explain, rather than throwing on the way in and blanking the whole route.
      return value;
   }
}

function base64Url(value: string): string {
   const bytes = new TextEncoder().encode(value);
   let binary = '';
   for (const byte of bytes) binary += String.fromCharCode(byte);
   return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(value: string): string {
   const padded = value.replace(/-/g, '+').replace(/_/g, '/');
   const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
   const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
   return new TextDecoder().decode(bytes);
}
